import { Component, OnInit, ViewChild, OnDestroy, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import {
  filter, switchMap, map, takeUntil, finalize,
  shareReplay,
  take
} from 'rxjs/operators';

import { IMemberAppConfig } from '@sondermind/configuration';
import {
  ClientInsuranceHttpService,
  IPatientInsurance
} from '@sondermind/data-access/client-insurance';
import { Logger } from '@sondermind/datadog-logging';
import {
  ClientCreditCardHttpService,
  ClientAccountPaymentMethod
} from '@sondermind/data-access/provider-facing/client-account-payment';
import { ProviderNetworkCompanyStore } from '@sondermind/data-access/provider-network-company';
import { SondermindApplicationService } from '@sondermind/data-access/sondermind-app-service';
import { ConfirmationModalComponent } from '@sondermind/features/modals/confirmation';
import { PatientInsuranceModalComponent } from '@sondermind/features/modals/patient-insurance';
import { NotificationToastService, ToastType } from '@sondermind/features/notification-toast';

import { IrisIconButtonTypes } from '@sondermind/iris/icon-button';
import { Patient } from '@sondermind/utilities/models-patient';
import { ProviderNetworkCompany } from '@sondermind/utilities/models-provider-network-company';

import { IMenuItemList } from '@sondermind/iris/menu';
import { IrisModalService, IrisModalSize } from '@sondermind/iris/modal';
import { ClientDetailPageService } from '@sondermind/features/provider-facing/client-detail';
import { IconSizes } from '@sondermind/iris/icon';
import { AddCcModalComponent, IAddCcModalInputs } from '@sondermind/features/modules/clients';
import { colorDecSandElevation1 } from '@sondermind/iris-design-tokens/design-tokens';
import { ProBonoModalComponent } from '../../components/pro-bono-modal/pro-bono-modal.component';

const INSURANCE_SAVED_MESSAGE = 'Patient insurance information saved successfully!';

@Component({
  selector: 'member-client-detail-payment-info',
  templateUrl: './client-detail-payment-info.component.html',
  styleUrls: ['./client-detail-payment-info.component.scss'],
  standalone: false
})
export class ClientDetailPaymentInfoPage implements OnInit, OnDestroy {
  @ViewChild(PatientInsuranceModalComponent)
    insModal: PatientInsuranceModalComponent;

  @ViewChild(ConfirmationModalComponent)
    confirmModal: ConfirmationModalComponent;

  private destroyedSubj: Subject<void>;
  destroyed$: Observable<void>;

  isLoading$ = new BehaviorSubject(true);

  patient: Patient;
  patient$: Observable<Patient>;
  patientCreditCards$: Observable<ClientAccountPaymentMethod[]>;
  patientInsurances: IPatientInsurance[] = [];

  usedProBono: number = 0;
  maxProBono: number = 0;
  remainingProBono: number = 0;

  defaulting = false;

  // Columns to be visible under credit card listing
  creditCardColumns = ['billing_contact', 'brand', 'last4', 'exp_date', 'last_charged', 'nickname', 'action'];
  creditCardMobileColumns = ['brand', 'last4', 'exp_date', 'nickname', 'action'];

  // Columns to be visible under insurance listing
  insuranceColumns = [
    'provider_network',
    'policy_holder',
    'group_number',
    'member_number',
    'complimentary_sessions',
    'action'
  ];
  insuranceMobileColumns = [
    'provider_network', 'policy_holder', 'member_number', 'complimentary_sessions', 'action'
  ];
  creditCardMenuItemsMap$: Observable<Map<number, IMenuItemList[]>>;
  creditCardMenuItemsMapForMobile$: Observable<Map<number, IMenuItemList[]>>;
  insuranceMenuItemsMap$: Observable<Map<number, IMenuItemList[]>>;

  canUseProBonoNpc$ = this.pncStore.getAll$().pipe(
    map((pncs) => pncs.some((pnc) => pnc.isProBono))
  );

  pncLookup$: Observable<Record<number, ProviderNetworkCompany>>;

  readonly isSaasEnabled = signal<boolean>(false);

  IrisIconButtonTypes = IrisIconButtonTypes;
  readonly iconSizes = IconSizes;

  constructor(
    private appService: SondermindApplicationService,
    public clientCreditCardService: ClientCreditCardHttpService<IMemberAppConfig>,
    private clientInsuranceHttp: ClientInsuranceHttpService,
    private clientDetailPageService: ClientDetailPageService,
    private pncStore: ProviderNetworkCompanyStore,
    private snackBar: MatSnackBar,
    private irisModalService: IrisModalService,
    private logger: Logger,
    private toastService: NotificationToastService
  ) {
    this.destroyedSubj = new Subject();
    this.destroyed$ = this.destroyedSubj.asObservable();
  }

  ngOnInit(): void {
    this.isSaasEnabled.set(this.appService.isSaasProvider());

    this.patient$ = this.clientDetailPageService.patient.pipe(
      filter((p) => !!p),
      map((p) => {
        this.patient = p;
        this.loadCreditCards();

        if (this.patient.personaClientId) {
          this.loadInsurances();
        }

        return p;
      })
    );

    this.pncLookup$ = this.pncStore.getLookup$();
  }

  ngOnDestroy(): void {
    this.destroyedSubj.next();
    this.destroyedSubj.complete();
  }

  addProBono(): void {
    const modalRef = this.irisModalService.open<
    ProBonoModalComponent, { patientId: number; currentCount: number; }, IPatientInsurance
    >(
      ProBonoModalComponent,
      {
        data: { patientId: this.patient.id, currentCount: this.maxProBono },
        size: IrisModalSize.MD,
        title: 'Benefits verification outage information'
      }
    );

    modalRef.afterClosed$().pipe(
      take(1),
      takeUntil(this.destroyed$)
    ).subscribe((res) => {
      if (res) {
        this.loadInsurances();
      }
    });
  }

  addCard(): void {
    const modalRef = this.irisModalService.open<AddCcModalComponent, IAddCcModalInputs, ClientAccountPaymentMethod>(
      AddCcModalComponent,
      {
        size: IrisModalSize.LG,
        title: 'Add credit card',
        data: { patient: this.patient },
        customStyles: {
          backgroundColor: colorDecSandElevation1
        }
      }
    );

    modalRef.afterClosed$()
      .pipe(take(1))
      .subscribe((response) => {
        if (response) {
          this.snackBar.open('Credit card added successfully!', 'Dismiss');
          this.loadCreditCards();
        }
      });
  }

  archiveCard(card: ClientAccountPaymentMethod): void {
    const confirm$ = this.confirmModal.openThen(
      'Confirm Delete',
      'Do you want to permanently delete this credit card?',
      'Yes, Delete',
      'warn'
    );

    confirm$
      .pipe(
        filter((p) => !!p),
        switchMap(() => this.clientCreditCardService.delete(card.id)),
        takeUntil(this.destroyed$)
      )
      .subscribe({
        next: () => {
          this.snackBar.open('Credit card deleted successfully!', 'Dismiss');
          this.loadCreditCards();
        },
        error: (err) => {
          const theError: string[] = err.system_errors ?? err.fields?.system_errors;
          this.snackBar.open(theError?.[0], 'Dismiss');
        }
      });
  }

  setAsActive(card: ClientAccountPaymentMethod): void {
    this.defaulting = true;
    this.clientCreditCardService.processingFor = card.id;

    this.clientCreditCardService.setActive(card.id).pipe(
      takeUntil(this.destroyed$)
    ).subscribe({
      next: () => {
        this.loadCreditCards();

        const { last4 } = card;
        this.snackBar.open(`Set as Default: xxxxxxxxxx${last4}`, 'Dismiss', { duration: 4000 });

        this.defaulting = false;
        this.clientCreditCardService.processingFor = null;
      },
      error: (err) => {
        const theError: string[] = err.system_errors ?? err.fields?.system_errors;
        this.snackBar.open(`Error: ${theError?.[0]}`, 'Dismiss', { duration: 5000 });
        this.defaulting = false;
        this.clientCreditCardService.processingFor = null;
      }
    });
  }

  addInsurance(): void {
    this.insModal.openForCreate()
      .subscribe((ins) => {
        if (ins) {
          this.toastService.showNotificationToast({
            type: ToastType.SUCCESS,
            message: INSURANCE_SAVED_MESSAGE
          });
          this.loadInsurances();
        }
      });
  }

  editInsurance(insurance: IPatientInsurance): void {
    this.clientInsuranceHttp.fetch$(insurance.id, {
      view: 'with_insurance_information'
    }).pipe(
      takeUntil(this.destroyed$)
    ).subscribe({
      next: (resp) => {
        if (!resp?.insuranceInformation) {
          this.toastService.showNotificationToast({
            type: ToastType.ERROR,
            message: 'Unable to load insurance details'
          });
          this.logger.error('Insurance fetch returned incomplete data', {
            insuranceId: insurance.id,
            hasInsuranceInformation: !!resp?.insuranceInformation
          });
          return;
        }
        this.insModal.openForViewEdit(resp)
          .subscribe((ins) => {
            if (ins) {
              this.toastService.showNotificationToast({
                type: ToastType.SUCCESS,
                message: INSURANCE_SAVED_MESSAGE
              });
              this.loadInsurances();
            }
          });
      },
      error: (err) => {
        this.toastService.showNotificationToast({
          type: ToastType.ERROR,
          message: 'Failed to load insurance details'
        });
        this.logger.error('Failed to fetch insurance for edit', { err, insuranceId: insurance.id });
      }
    });
  }

  get canSubmitSuperBills(): boolean {
    return this.appService.currentUser.canSubmitSuperbill;
  }

  loadCreditCards(): void {
    this.isLoading$.next(true);

    let options = {};
    if (this.patient.personaClientId) {
      options = { filter: [{ key: 'persona_client_id', value: this.patient.personaClientId.toString() }] };
    } else {
      options = { filter: [{ key: 'patient_id', value: this.patient.id.toString() }] };
    }

    this.patientCreditCards$ = this.clientCreditCardService
      .list(options)
      .pipe(
        map((ccList) => {
          this.isLoading$.next(false);
          return ccList;
        })
      );

    this.createCreditCardItemMenu();
  }

  loadInsurances(): void {
    this.isLoading$.next(true);

    this.clientInsuranceHttp.list$({
      filter: { key: 'persona_client_id', value: this.patient.personaClientId.toString() },
      view: 'with_provider_network_detail'
    }).pipe(
      finalize(() => this.isLoading$.next(false)),
      takeUntil(this.destroyed$)
    ).subscribe({
      next: (insList) => {
        // redemption lives with normal insurances, pro-bono is special
        this.patientInsurances = insList.filter((i) => !i.providerNetworkCompany.isProBono);

        // there should only ever be one pro-bono for a given patient (client+provider)
        // but since it's just another card, we can't guarantee - so sum everything up instead
        const proBono = insList.filter((i) => i.providerNetworkCompany.isProBono);
        this.maxProBono = proBono.reduce((sum, p) => sum + p.maxRedemptions, 0);
        this.usedProBono = proBono.reduce((sum, p) => sum + p.usedRedemptions, 0);
        this.remainingProBono = proBono.reduce((sum, p) => sum + p.remainingRedemptions, 0);

        this.createMenuitemForInsurance();
      },
      error: () => {
        this.patientInsurances = [];
      }
    });
  }

  createCreditCardItemMenu(): void {
    this.patientCreditCards$.pipe(
      shareReplay(1)
    ).subscribe((cards) => {
      const cardsMap = new Map(
        cards.map((card) => [
          card.id,
          [
            {
              label: 'Set Default',
              action: () => this.setAsActive(card),
              dataTest: 'favorite',
              icon: 'care'
            },
            {
              label: 'Delete',
              action: () => this.archiveCard(card),
              dataTest: 'delete',
              icon: 'cancel'
            }
          ]
        ])
      );

      const menuItemsMapForMobile = new Map(
        cards.map((card) => [
          card.id,
          [
            {
              label: 'Delete',
              action: () => this.archiveCard(card),
              dataTest: 'delete',
              icon: 'cancel'
            }
          ]
        ])
      );

      // Assign the map to the observable
      this.creditCardMenuItemsMap$ = of(cardsMap);
      this.creditCardMenuItemsMapForMobile$ = of(menuItemsMapForMobile);
    });
  }

  createMenuitemForInsurance(): void {
    const menuItems = new Map<number, IMenuItemList[]>();

    this.patientInsurances.forEach((insurance) => {
      menuItems.set(insurance.id, [{
        label: insurance.canEdit ? 'Edit' : 'View',
        action: () => this.editInsurance(insurance),
        dataTest: 'edit',
        icon: insurance.canEdit ? 'edit' : 'access_time'
      }]);
    });

    this.insuranceMenuItemsMap$ = of(menuItems);
  }
}
